import { useState } from "react";
import { t } from 'i18next';
import { accountService } from "@/projects/admin/services/account-service";
import { accountRelationService } from "@/projects/admin/services/account-relation-service";
import DataGridWrapper from "@/core/wrappers/data-grid-wrapper";
import { createCustomStore } from "@/core/wrappers/custom-store-wrapper";



function AccountRelation(props: any) {
    const { accountId } = props;

    const [mainStore] = useState(createCustomStore({
        key: ['fromId', 'toId'],
        load: accountRelationService.getAccountRelations,
        byKey: (key, loadOptions) => {
            if (key) {
                loadOptions.filter = [["FromId", "=", key.fromId], "and", ["ToId", "=", key.toId]];
            }
        },
        onLoading(loadOptions) {
            if (accountId) {
                loadOptions.filter = [["FromId", "=", accountId], "or", ["ToId", "=", accountId]]
            }
        },
        insert: accountRelationService.createAccountRelation,
        update: accountRelationService.updateAccountRelation,
        remove: accountRelationService.deleteAccountRelation,
    }));

    const [retailerStore] = useState(createCustomStore({
        key: 'id',
        load: accountService.lookupAccounts,
        byKey: (key, loadOptions) => {
            if (key) {
                loadOptions.filter = ["id", "=", key];
            }
        },
        onLoading(loadOptions) {
            const accountTypeFilter = ["AccountType", "=", "Retailer"];
            if (loadOptions.filter) {
                loadOptions.filter = [loadOptions.filter, "and", accountTypeFilter];
            } else {
                loadOptions.filter = accountTypeFilter;
            }
        },
        onLoaded(result: any) {
            const currentAccount = result.data.find((item: any) => item.id === accountId);
            if (currentAccount) {
                result.data = [currentAccount];
            }
        },
        useDefaultSearch: true,
    }));

    const [supplierStore] = useState(createCustomStore({
        key: 'id',
        load: accountService.lookupAccounts,
        byKey: (key, loadOptions) => {
            if (key) {
                loadOptions.filter = ["id", "=", key];
            }
        },
        onLoading(loadOptions) {
            const accountTypeFilter = ["AccountType", "=", "Supplier"];
            if (loadOptions.filter) {
                loadOptions.filter = [loadOptions.filter, "and", accountTypeFilter];
            } else {
                loadOptions.filter = accountTypeFilter;
            }
        },

        onLoaded(result: any) {
            const currentAccount = result.data.find((item: any) => item.id === accountId);
            if (currentAccount) {
                result.data = [currentAccount];
            }
        },
        useDefaultSearch: true,
    }));


    const handleRowUpdating = async (e: any) => {
        e.newData = { ...e.oldData, ...e.newData }
    };

    function onEditorPreparing(e: any) {
        const noUpdating = ['fromId', 'toId'];

        if (noUpdating.includes(e.dataField) && e.row && !e.row.isNewRow) {
            e.editorOptions.disabled = true;
        }


        if (e.parentType === "dataRow" && e.dataField === "isLocationShared") {
            e.editorOptions.onValueChanged = function (args: any) {
                e.setValue && e.setValue(args.value, e)
                e.component.cellValue(e.row.rowIndex, "defaultStockShare", true);
                e.component.cellValue(e.row.rowIndex, "defaultSalesShare", true);
                handleValue(args.value, e.row.rowIndex, e.component);

            };
        }



    }
    function handleValue(isChecked: boolean, rowIndex: number, component: any) {
        if (component) {

            // DefaultStockShare hücresini güncelle
            const stockShareCell = component.getCellElement(rowIndex, component.columnOption("defaultStockShare").index);
            if (stockShareCell) {
                const stockCheckbox = stockShareCell.querySelector(".dx-checkbox");
                if (stockCheckbox) {
                    if (!isChecked) {
                        stockCheckbox.classList.remove("dx-state-disabled");
                        stockCheckbox.setAttribute("aria-disabled", "false");
                    } else {
                        stockCheckbox.classList.add("dx-state-disabled");
                        stockCheckbox.setAttribute("aria-disabled", "true");
                    }
                }
            }

            const salesShareCell = component.getCellElement(rowIndex, component.columnOption("defaultSalesShare").index);
            if (salesShareCell) {
                const salesCheckbox = salesShareCell.querySelector(".dx-checkbox");
                if (salesCheckbox) {
                    if (!isChecked) {
                        salesCheckbox.classList.remove("dx-state-disabled");
                        salesCheckbox.setAttribute("aria-disabled", "false");
                    } else {
                        salesCheckbox.classList.add("dx-state-disabled");
                        salesCheckbox.setAttribute("aria-disabled", "true");
                    }
                }
            }
        }
    }
    function onContentReady(e: any) {

        const rows = e.component.getVisibleRows()
        rows.forEach((row: any) => {
            if (row.data.isLocationShared) {
                handleValue(true, row.rowIndex, e.component)
                row.cells.forEach((cell: any) => {
                    if (cell.column.dataField === "defaultStockShare" || cell.column.dataField === "defaultSalesShare") {
                        row.data.defaultStockShare = true
                        row.data.defaultSalesShare = true
                        cell.update(row)
                    }
                });
            }
        });
    }

    return (
        <DataGridWrapper
            datasource={mainStore}
            permissonCode="Admin.Account.Relationship"
            remoteOperations={true}
            onRowUpdating={handleRowUpdating}
            onEditorPreparing={onEditorPreparing}
            onContentReady={onContentReady}
            pagerVisible={true}
            enableCustomToolbarContent={false}
            searchPanelVisible={true}
            columnChooser={false}
            filterVisible={true}
            headerFilterVisible={true}         
            editingConfig={{
                mode: 'batch',
                allowAdding: true,
                allowDeleting: true,
                allowUpdating: true,
            }}
            columns={[
                {
                    dataField: 'fromId',
                    caption: t('retailer'),
                    required: true,
                    lookup: {
                        dataSource: retailerStore,
                        valueExpr: "id",
                        displayExpr: "name"
                    }
                },
                {
                    dataField: 'toId',
                    caption: t('supplier'),
                    required: true,
                    lookup: {
                        dataSource: supplierStore,
                        valueExpr: "id",
                        displayExpr: "name"
                    }
                },
                {
                    dataField: 'fromValue',
                    caption: t('retailerCode'),
                },
                {
                    dataField: 'toValue',
                    caption: t('supplierCode'),
                },
                {
                    dataField: 'isLocationShared',
                    caption: t('isLocationShared'),
                    dataType: 'boolean',
                    calculateCellValue: (rowData: any) => {
                        return rowData.isLocationShared || false;
                    },
                },
                {
                    dataField: 'defaultStockShare',
                    caption: t('defaultStockShare'),
                    dataType: 'boolean',
                    calculateCellValue: (rowData: any) => {
                        return rowData.defaultStockShare || false;
                    },
                    width: 270
                },
                {
                    dataField: 'defaultSalesShare',
                    caption: t('defaultSalesShare'),
                    dataType: 'boolean',
                    calculateCellValue: (rowData: any) => {
                        return rowData.defaultSalesShare || false;
                    },
                    width: 270
                },
                {
                    dataField: 'defaultPriceShare',
                    caption: t('PriceShare'),
                    dataType: 'boolean',
                    calculateCellValue: (rowData: any) => {
                        return rowData.defaultPriceShare || false;
                    },
                    width: 270
                }
            ]}
        />
    );
}
export default AccountRelation